/**
 * ApplicationScope GraphQL Operations
 *
 * Queries, mutations, and TypeScript types for application scope management.
 */

// ===== TypeScript Types =====

export type ApplicationScopeStatus = 'ACTIVE' | 'DISABLED' | 'DEPRECATED';

export interface ApplicationScope {
  scopeId: string;
  displayName: string;
  description: string | null;
  icon: string | null;
  affordanceDomains: string[];
  orderHint: number | null;
  scopeStatus: ApplicationScopeStatus;
}

export interface CreateApplicationScopeInput {
  scopeId: string;
  displayName: string;
  description?: string;
  icon?: string;
  affordanceDomains?: string[];
  orderHint?: number;
}

export interface UpdateApplicationScopeInput {
  displayName?: string;
  description?: string;
  icon?: string;
  affordanceDomains?: string[];
  orderHint?: number;
  scopeStatus?: ApplicationScopeStatus;
}

// ===== Response Types =====

export interface GetVisibleApplicationScopesResponse {
  visibleApplicationScopes: ApplicationScope[];
}

export interface GetApplicationScopeResponse {
  applicationScope: ApplicationScope | null;
}

export interface GetApplicationScopesResponse {
  applicationScopes: ApplicationScope[];
}

export interface CreateApplicationScopePayload {
  createApplicationScope: { scope: ApplicationScope };
}

export interface UpdateApplicationScopePayload {
  updateApplicationScope: { scope: ApplicationScope };
}

export interface ReorderApplicationScopeInput {
  scopeId: string;
  orderHint: number;
}

export interface ReorderApplicationScopesPayload {
  reorderApplicationScopes: { scopes: ApplicationScope[] };
}

// ===== Fragments =====

const SCOPE_FIELDS = `
  scopeId
  displayName
  description
  icon
  affordanceDomains
  orderHint
  scopeStatus
`;

// ===== Queries =====

export const GET_VISIBLE_APPLICATION_SCOPES = `
  query GetVisibleApplicationScopes($previewRoleId: ID) {
    visibleApplicationScopes(previewRoleId: $previewRoleId) {
      ${SCOPE_FIELDS}
    }
  }
`;

export const GET_APPLICATION_SCOPE = `
  query GetApplicationScope($scopeId: String!) {
    applicationScope(scopeId: $scopeId) {
      ${SCOPE_FIELDS}
    }
  }
`;

export const GET_APPLICATION_SCOPES = `
  query GetApplicationScopes {
    applicationScopes {
      ${SCOPE_FIELDS}
    }
  }
`;

// ===== Mutations =====

export const CREATE_APPLICATION_SCOPE = `
  mutation CreateApplicationScope($input: CreateApplicationScopeInput!) {
    createApplicationScope(input: $input) {
      scope {
        ${SCOPE_FIELDS}
      }
    }
  }
`;

export const UPDATE_APPLICATION_SCOPE = `
  mutation UpdateApplicationScope($scopeId: String!, $input: UpdateApplicationScopeInput!) {
    updateApplicationScope(scopeId: $scopeId, input: $input) {
      scope {
        ${SCOPE_FIELDS}
      }
    }
  }
`;

export const REORDER_APPLICATION_SCOPES = `
  mutation ReorderApplicationScopes($input: [ReorderApplicationScopeInput!]!) {
    reorderApplicationScopes(input: $input) {
      scopes {
        ${SCOPE_FIELDS}
      }
    }
  }
`;
